import { CalendarDays, CalendarRange, Check, ClipboardList, Plus, Users, Wallet } from "lucide-react";
import type { CSSProperties } from "react";

import { kilos } from "@/features/portal/components/app-screens";
import { Monogram } from "@/features/portal/components/portal-ui";
import { BrandMark } from "@/features/site/components/brand-mark";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The coach app wears its own colour, so the laptop reads as the other app, not this one. Zoomed
 * in, as a screenshot for a page would be: at laptop scale, 1:1 type is too small to read.
 */
const ZOOM = 1.25;
const coachTheme = {
  "--primary": "oklch(0.84 0.11 230)",
  "--primary-foreground": "oklch(0.2 0.03 240)",
  width: 1140 / ZOOM,
  height: 716 / ZOOM,
  transform: `scale(${ZOOM})`,
} as CSSProperties;

/** The coach app's sidebar, folded to its icons the way a narrow window shows it. */
const NAV = [
  { id: "today", icon: CalendarDays },
  { id: "schedule", icon: CalendarRange },
  { id: "clients", icon: Users, active: true },
  { id: "plans", icon: ClipboardList },
  { id: "finances", icon: Wallet },
];

const PLAN = [
  { id: "squat", name: () => m.features_ex_squat(), sets: 4, reps: 8, weight: 82.5 },
  { id: "row", name: () => m.features_ex_row(), sets: 3, reps: 10, weight: 60 },
  { id: "press", name: () => m.features_ex_press(), sets: 3, reps: 10, weight: 26, typing: true },
];

const FEED = [
  { id: "s2", what: () => m.workout_set_label({ number: "2" }), weight: 82.5, reps: 8, when: () => m.features_desk_now() },
  { id: "s1", what: () => m.workout_set_label({ number: "1" }), weight: 80, reps: 8, when: () => m.features_desk_ago({ minutes: 3 }) },
];

const field = "grid h-9 min-w-[44px] place-items-center rounded-[10px] bg-foreground/[0.06] px-3 font-semibold tabular-nums";

/**
 * The coach's side of a live session, as the coach app shows it on a laptop: the plan being
 * written on the left, and each set the athlete logs arriving on the right. 1140 × 716.
 */
export const CoachDesk = () => (
  <div style={coachTheme} className="absolute top-0 left-0 flex origin-top-left pt-[22px]">
    <aside className="flex w-[68px] shrink-0 flex-col items-center gap-1.5 border-r border-foreground/[0.06] pt-4">
      <BrandMark className="mb-4 size-8" />
      {NAV.map((item) => (
        <span
          key={item.id}
          className={cn(
            "grid size-10 place-items-center rounded-[12px] text-muted-foreground",
            item.active && "bg-foreground/[0.08] text-foreground",
          )}
        >
          <item.icon className="size-[18px]" />
        </span>
      ))}
    </aside>
    <div className="flex min-w-0 flex-1 flex-col px-6 pt-5">
      <div className="flex items-center gap-4">
        <Monogram name={m.features_sample_athlete_full()} className="size-12 rounded-[16px] text-[16px]" />
        <div className="flex-1">
          <p className="text-[13px] text-muted-foreground">{m.features_sample_athlete_full()}</p>
          <h4 className="flex items-center gap-3 text-[22px] font-semibold tracking-[-0.02em] whitespace-nowrap">
            {m.coached_live_title()}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap text-primary">
              <i className="size-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
              {m.coached_live_badge()}
            </span>
          </h4>
        </div>
        <span className="text-[15px] font-semibold text-muted-foreground tabular-nums">24:18</span>
        <span className="flex h-10 items-center rounded-full bg-foreground px-5 text-[14px] font-semibold whitespace-nowrap text-background">
          {m.workout_finish_workout()}
        </span>
      </div>
      <div className="mt-5 grid min-h-0 flex-1 grid-cols-[1.35fr_1fr] gap-4 pb-6">
        <section className="rounded-[20px] bg-foreground/[0.035] p-5 ring-1 ring-foreground/[0.06] ring-inset">
          <p className="text-[12px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            {m.features_desk_plan()}
          </p>
          <ul className="mt-4 grid gap-2.5">
            {PLAN.map((row, index) => (
              <li
                key={row.id}
                className={cn(
                  "flex items-center gap-3 rounded-[14px] bg-background/60 p-3 text-[14px]",
                  row.typing && "ring-2 ring-primary ring-inset",
                )}
              >
                <span className="grid size-8 place-items-center rounded-full bg-foreground/[0.07] text-[13px] font-semibold">
                  {index + 1}
                </span>
                <span className="flex-1 truncate font-semibold">
                  {row.name()}
                  {row.typing ? <i className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-pulse bg-primary" /> : null}
                </span>
                <span className={field}>{row.sets}</span>
                <span className="text-muted-foreground">×</span>
                <span className={field}>{row.reps}</span>
                <span className={cn(field, "min-w-[72px]")}>{kilos(row.weight)} kg</span>
              </li>
            ))}
          </ul>
          <span className="mt-3 flex h-11 items-center justify-center gap-2 rounded-[14px] border border-dashed border-foreground/15 text-[14px] text-muted-foreground">
            <Plus className="size-4" />
            {m.features_desk_add()}
          </span>
        </section>
        <section className="flex flex-col rounded-[20px] bg-foreground/[0.035] p-5 ring-1 ring-foreground/[0.06] ring-inset">
          <p className="text-[12px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            {m.features_desk_feed({ name: m.features_sample_athlete() })}
          </p>
          <ul className="mt-4 grid gap-2.5">
            {FEED.map((set, index) => (
              <li
                key={set.id}
                className={cn(
                  "flex items-center gap-3 rounded-[14px] p-3",
                  index === 0 ? "bg-primary/12 ring-1 ring-primary/40 ring-inset" : "bg-background/60",
                )}
              >
                <span className="grid size-8 place-items-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-4" strokeWidth={3} />
                </span>
                <span className="min-w-0 flex-1 leading-tight">
                  <b className="block truncate text-[14px] font-semibold">{m.features_ex_squat()}</b>
                  <small className="text-[13px] text-muted-foreground tabular-nums">
                    {set.what()} · {m.workout_values_weighted({ weight: kilos(set.weight), reps: set.reps })}
                  </small>
                </span>
                <small className="text-[12px] text-muted-foreground">{set.when()}</small>
              </li>
            ))}
          </ul>
          <div className="mt-auto grid grid-cols-2 gap-2.5 pt-4">
            {[
              { id: "volume", label: m.features_desk_volume(), value: "1 300 kg" },
              { id: "sets", label: m.features_desk_sets(), value: "2 / 10" },
            ].map((stat) => (
              <span key={stat.id} className="rounded-[14px] bg-background/60 p-3">
                <small className="block text-[12px] text-muted-foreground">{stat.label}</small>
                <b className="text-[20px] font-semibold whitespace-nowrap tabular-nums">{stat.value}</b>
              </span>
            ))}
          </div>
        </section>
      </div>
    </div>
  </div>
);
